import { isNative } from "./platform";
import { APP_ENV } from "./runtime-env";

/**
 * QA / non-production environment detection.
 *
 * History — the old `hostname.includes("-staging.")` check was scattered
 * across three places (top-bar "Raise Ticket" button, the tickets page
 * production guard, and the modal's auto-populated `environment`
 * field). It missed tenant subdomains like `<school>.staging.bytego.ai`
 * (dot, not dash), which is why the user reported "Raise Defect link
 * sometimes doesn't appear in staging" — it appeared on the apex
 * `campushub-staging.bytego.ai` but vanished the moment they logged into
 * a tenant like `holycross.staging.bytego.ai`.
 *
 * Patterns we treat as non-prod (case-insensitive):
 *   localhost                        — Vite dev server
 *   127.0.0.1                        — same
 *   *.local                          — Bonjour / LAN dev
 *   *staging*                        — covers both `-staging.` and `.staging.`
 *   *.dev.*  /  *dev-*  /  *-dev.*   — feature preview / dev envs
 *   *.preview.*                      — Vercel-style preview URLs
 *
 * Production hosts (e.g. `app.bytego.ai`, `<school>.bytego.ai`) match
 * none of these and return `false`.
 */
/**
 * CAMP-2026-0966 — inside the Capacitor shell the WebView's hostname is
 * ALWAYS `localhost` (androidScheme/iosScheme https, `server.hostname` unset),
 * so the hostname rules below classed every store build — production
 * included — as a local dev box and put the QA Board in the family's menu.
 * On a native build the environment is the one stamped at build time
 * (`VITE_ENV_NAME`, scripts/build-native.sh): only `dev` and `staging` builds
 * are QA environments. An unstamped native build is treated as production.
 */
export function nativeQaEnv(appEnv: string): boolean {
  return appEnv === "dev" || appEnv === "staging";
}

export function isQaEnv(): boolean {
  if (typeof window === "undefined") return false;
  if (isNative()) return nativeQaEnv(APP_ENV);
  const h = window.location.hostname.toLowerCase();
  if (h === "localhost" || h === "127.0.0.1") return true;
  if (h.endsWith(".local")) return true;
  if (h.includes("staging")) return true;
  // Be conservative with "dev" — match it only as a clear subdomain
  // segment, not as part of an arbitrary tenant name like "devansh".
  if (/(^|[.-])dev([.-]|$)/.test(h)) return true;
  if (h.includes(".preview.")) return true;
  return false;
}

/** Best-effort environment label for the `environment` ticket field. */
export function qaEnvLabel(): string {
  if (typeof window === "undefined") return "unknown";
  if (isNative()) return APP_ENV === "web" ? "production" : APP_ENV;
  const h = window.location.hostname.toLowerCase();
  if (h === "localhost" || h === "127.0.0.1" || h.endsWith(".local")) return "local";
  if (h.includes("staging")) return "staging";
  if (/(^|[.-])dev([.-]|$)/.test(h)) return "dev";
  if (h.includes(".preview.")) return "preview";
  return "production";
}
